// src/pages/StripePaymentPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../api/axiosClient';

const StripePaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needsBusinessInfo, setNeedsBusinessInfo] = useState(false);
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessCity, setBusinessCity] = useState('');
  const [businessCountry, setBusinessCountry] = useState('');
  const hasAttempted = useRef(false);
  const storageKey = 'hairchitech.checkoutBusinessLocation';

  const plan = searchParams.get('plan') || 'salon';
  const annual = searchParams.get('annual') === '1';
  const addon = searchParams.get('addon') === '1';

  const { planKey, businessType } = useMemo(() => {
    let key = 'salon_monthly';
    let type = plan === 'barbershop' ? 'barbershop' : 'salon';

    if (plan === 'barbershop') {
      key = annual ? 'barbershop_annual' : 'barbershop_monthly';
    } else if (plan === 'salonPremium' || addon) {
      key = annual ? 'salon_premium_annual' : 'salon_premium_monthly';
    } else {
      key = annual ? 'salon_annual' : 'salon_monthly';
    }

    return { planKey: key, businessType: type };
  }, [plan, annual, addon]);

  const detectedTimezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_) {
      return 'UTC';
    }
  })();

  const createCheckoutSession = useCallback(async ({ address, city, country }) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post('/payments-new/checkout-session', {
        planKey,
        businessType,
        seats: 1,
        businessAddress: address,
        businessCity: city,
        businessCountry: country,
        timezone: detectedTimezone,
      });
      const { checkoutUrl } = res.data;
      window.location.href = checkoutUrl;
    } catch (err) {
      if (err?.response?.status === 401) {
        // Not authenticated – use in-app sign-in panel
        navigate('/?auth=1');
        return;
      }
      if (err?.response?.status === 400 && err?.response?.data?.required) {
        setNeedsBusinessInfo(true);
      }
      console.error('Stripe checkout error', err);
      setError(err?.response?.data?.error || 'Failed to start checkout.');
    } finally {
      setSubmitting(false);
    }
  }, [businessType, detectedTimezone, navigate, planKey]);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;
    let stored = null;
    try {
      const raw = localStorage.getItem(storageKey);
      stored = raw ? JSON.parse(raw) : null;
    } catch (_) {
      stored = null;
    }
    if (stored?.address && stored?.city && stored?.country) {
      setBusinessAddress(stored.address);
      setBusinessCity(stored.city);
      setBusinessCountry(stored.country);
      createCheckoutSession({
        address: stored.address,
        city: stored.city,
        country: stored.country,
      });
    } else {
      setNeedsBusinessInfo(true);
    }
  }, [createCheckoutSession]);

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    const address = businessAddress.trim();
    const city = businessCity.trim();
    const country = businessCountry.trim();
    if (!address || !city || !country) {
      setError('Please enter your business address, city, and country.');
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify({ address, city, country }));
    } catch (_) {
      // ignore storage failures
    }
    await createCheckoutSession({ address, city, country });
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900" />
      <div className="absolute inset-0 bg-gradient-to-r from-amber-900/30 via-yellow-900/20 to-transparent" />

      <div className="relative z-10 max-w-md w-full mx-auto text-center px-6 py-12 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
        <div className="mx-auto mb-6 w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#7d6633' }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9.414V6a1 1 0 112 0v2.586l1.707 1.707a1 1 0 01-1.414 1.414L10 10.414l-1.293 1.293a1 1 0 11-1.414-1.414L9 8.586z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {needsBusinessInfo ? 'Confirm Your Business Location' : 'Redirecting to Secure Checkout'}
        </h1>
        <p className="text-gray-300 mb-6">
          {needsBusinessInfo
            ? 'We need your business location to set up your timezone.'
            : 'Please wait while we prepare your subscription.'}
        </p>

        {!needsBusinessInfo && (
          <div className="flex items-center justify-center space-x-2 mb-6">
            <span className="w-3 h-3 rounded-full animate-bounce" style={{ background: '#c9a038', animationDelay: '0ms' }} />
            <span className="w-3 h-3 rounded-full animate-bounce" style={{ background: '#c9a038', animationDelay: '150ms' }} />
            <span className="w-3 h-3 rounded-full animate-bounce" style={{ background: '#c9a038', animationDelay: '300ms' }} />
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm">{error}</div>
        )}

        {needsBusinessInfo && (
          <form onSubmit={handleBusinessSubmit} className="mt-6 space-y-4 text-left">
            <div>
              <label className="block text-xs font-medium text-white/80 mb-1">Business address</label>
              <input
                type="text"
                value={businessAddress}
                onChange={(e) => setBusinessAddress(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white bg-white/10 border border-white/10 focus:outline-none focus:border-amber-400/70"
                placeholder="Street address"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/80 mb-1">City</label>
              <input
                type="text"
                value={businessCity}
                onChange={(e) => setBusinessCity(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white bg-white/10 border border-white/10 focus:outline-none focus:border-amber-400/70"
                placeholder="City"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/80 mb-1">Country</label>
              <input
                type="text"
                value={businessCountry}
                onChange={(e) => setBusinessCountry(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white bg-white/10 border border-white/10 focus:outline-none focus:border-amber-400/70"
                placeholder="Country"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium text-zinc-900 shadow hover:-translate-y-0.5 transition disabled:opacity-60"
              style={{ backgroundColor: '#7d6633' }}
            >
              {submitting ? 'Starting checkout…' : 'Continue to checkout'}
            </button>
          </form>
        )}

        <div className="mt-8">
          <button
            onClick={() => navigate('/pricing')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white/90 hover:text-white border border-white/20 hover:bg-white/10 transition-colors"
          >
            Go back to Pricing
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripePaymentPage;
